import { z } from "zod";
import { pageQuery, trimmed, uuid } from "./common";

/**
 * Защита от злоупотреблений (`addon.fraud`, ТЗ 15 §3): правила проверяют операции за период и создают сигналы (алерты)
 * для владельца. Порог правила — `threshold` (единица — в `unit`), можно менять в настройках компании, правило — отключить.
 */
export const FRAUD_SEVERITIES = ["low", "medium", "high"] as const;
export type FraudSeverity = (typeof FRAUD_SEVERITIES)[number];
/** Вес сигнала в risk score сотрудника */
export const FRAUD_WEIGHT: Record<FraudSeverity, number> = { low: 1, medium: 3, high: 5 };

type Rule = { severity: FraudSeverity; threshold?: { default: number; min: number; max: number; unit: "count" | "percent" | "days" | "sigma" } };
export const FRAUD_RULES = {
  /** Больше N удалённых позиций у сотрудника за день */
  void_many: { severity: "medium", threshold: { default: 5, min: 1, max: 100, unit: "count" } },
  /** Удаление позиции, которую уже готовила кухня */
  void_after_kitchen: { severity: "high" },
  /** Отмена заказа, отправленного на кухню */
  cancel_after_kitchen: { severity: "high" },
  /** Больше N возвратов у сотрудника за день */
  refunds_many: { severity: "high", threshold: { default: 2, min: 1, max: 50, unit: "count" } },
  /** Возврат наличными заказа, оплаченного только картой/QR */
  refund_cash_for_card: { severity: "high" },
  /** Ручные скидки сотрудника за день — больше N% суммы его заказов */
  discount_share: { severity: "medium", threshold: { default: 10, min: 1, max: 100, unit: "percent" } },
  /** Позиция продана дешевле цены меню */
  price_below_menu: { severity: "low" },
  /** Списание больше среднего по филиалу на N стандартных отклонений (за 90 дней) */
  writeoff_outlier: { severity: "medium", threshold: { default: 2, min: 1, max: 5, unit: "sigma" } },
  /** Недостача наличных при закрытии смены (повторная за 30 дней — high) */
  cash_shortage: { severity: "medium" },
  /** Продажа в долг без погашений дольше N дней */
  credit_unpaid: { severity: "medium", threshold: { default: 30, min: 7, max: 365, unit: "days" } },
} as const satisfies Record<string, Rule>;
export type FraudRule = keyof typeof FRAUD_RULES;
export const FRAUD_RULE_CODES = Object.keys(FRAUD_RULES) as FraudRule[];

export const FRAUD_STATUSES = ["new", "reviewed", "dismissed", "confirmed"] as const;
export type FraudStatus = (typeof FRAUD_STATUSES)[number];

const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });
const rules = z.enum(FRAUD_RULE_CODES as [FraudRule, ...FraudRule[]]);

export const fraudAlertsQuery = pageQuery.extend({
  status: z.enum(FRAUD_STATUSES).optional(),
  severity: z.enum(FRAUD_SEVERITIES).optional(),
  rule: rules.optional(),
  userId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export const fraudSummaryQuery = z
  .object({ from: isoDate, to: isoDate })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 366 * 86_400_000, { error: "Период не больше года", path: ["to"] });
export const fraudScanSchema = z
  .strictObject({ from: isoDate, to: isoDate })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 92 * 86_400_000, { error: "Не больше 3 месяцев за раз", path: ["to"] });
export const fraudReviewSchema = z.strictObject({
  status: z.enum(["reviewed", "dismissed", "confirmed"]),
  comment: trimmed(1, 1000).nullable().optional(),
});
export type FraudReviewInput = z.input<typeof fraudReviewSchema>;
export const fraudSettingsSchema = z.strictObject({
  rules: z.partialRecord(rules, z.strictObject({ enabled: z.boolean(), threshold: z.number().min(0).max(1000).optional() })),
});
export type FraudSettings = z.output<typeof fraudSettingsSchema>;

/** Действующий порог правила: из настроек компании (в допустимых пределах) или по умолчанию. */
export function fraudThreshold(rule: FraudRule, settings: FraudSettings | null | undefined): number | null {
  const t = (FRAUD_RULES[rule] as Rule).threshold;
  if (!t) return null;
  const v = settings?.rules?.[rule]?.threshold;
  return v === undefined ? t.default : Math.min(t.max, Math.max(t.min, v));
}
export const fraudRuleEnabled = (rule: FraudRule, settings: FraudSettings | null | undefined) => settings?.rules?.[rule]?.enabled !== false;
